import type { OnChanges, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  Input,
} from '@angular/core';
import { trackByValue } from '@freelancer/ui/helpers';
import { IconColor, IconSize } from '@freelancer/ui/icon';
import type { RatingTextColor } from '@freelancer/ui/rating';
import { textVariants } from '@freelancer/ui/text';
import { FontColor, TextSize } from '@freelancer/ui/text';
import { EarningsSize } from './earnings.types';

@Component({
  standalone: false,
  selector: 'fl-earnings',
  template: `
    <div
      class="EarningsContainer"
      [class.mobile:hide]="compact || !!mobileCompact"
      [class.tablet:hide]="compact"
      [class.desktop:hide]="compact"
    >
      @if (flipValue) {
        <ng-container *ngTemplateOutlet="earningsText"></ng-container>
      }
      <fl-icon
        class="DollarIcon"
        [name]="'ui-dollar-alt'"
        [size]="size"
        [sizeTablet]="sizeTablet"
        [sizeDesktop]="sizeDesktop"
        [sizeDesktopLarge]="sizeDesktopLarge"
        [color]="IconColor.INHERIT"
        [attr.data-flip-value]="flipValue"
        [useIconFont]="true"
        [fillIconFont]="true"
      ></fl-icon>

      @for (block of blocks; track trackByValue($index, block)) {
        <div
          class="ValueBlock"
          [ngStyle]="{ background: block }"
          [attr.data-size]="size"
          [attr.data-size-tablet]="sizeTablet"
          [attr.data-size-desktop]="sizeDesktop"
          [attr.data-size-desktop-large]="sizeDesktopLarge"
          [class.desktop_large:show]="desktopSmallCompact"
        ></div>
      }
      @if (!flipValue) {
        <div class="RightEarningsText">
          <ng-container *ngTemplateOutlet="earningsText"></ng-container>
        </div>
      }
    </div>

    <div
      class="EarningsContainer"
      [class.mobile:hide]="!compact && !mobileCompact"
      [class.tablet:hide]="!compact"
      [class.desktop:hide]="!compact"
    >
      <fl-icon
        class="DollarIcon mr-xxxsmall"
        [name]="'ui-dollar-alt'"
        [size]="size"
        [sizeTablet]="sizeTablet"
        [sizeDesktop]="sizeDesktop"
        [sizeDesktopLarge]="sizeDesktopLarge"
        [color]="IconColor.INHERIT"
        [useIconFont]="true"
        [fillIconFont]="true"
      ></fl-icon>
      <ng-container *ngTemplateOutlet="earningsText"></ng-container>
    </div>
    <ng-template #earningsText>
      <p
        class="EarningsText {{ earningsTextClass }}"
        [attr.data-size]="size"
        [attr.data-size-tablet]="sizeTablet"
        [attr.data-size-desktop]="sizeDesktop"
        [attr.data-size-desktop-large]="sizeDesktopLarge"
        [attr.data-compact]="compact"
      >
        {{ earnings | number: '1.1-1' }}
      </p>
    </ng-template>
  `,
  styleUrls: ['./earnings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EarningsComponent implements OnInit, OnChanges {
  IconColor = IconColor;
  trackByValue = trackByValue;

  earningsTextSize: TextSize = TextSize.BODY_16;
  earningsTextSizeTablet?: TextSize | undefined;
  earningsTextSizeDesktop?: TextSize | undefined;
  earningsTextSizeDesktopLarge?: TextSize | undefined;

  earningsTextClass: string;

  blocks: string[] = [];
  @Input() compact = false;
  @Input() textColor: RatingTextColor = 'foreground';
  /** Automatically sets compact on mobile */
  @Input() mobileCompact?: boolean;
  @Input() desktopSmallCompact?: boolean;
  @Input() earnings: number;
  /**
   * If flipValue=true, earnings component value is on the left side
   * Else, earnings component value is on the right side
   */
  @Input() flipValue = false;

  @HostBinding('attr.data-size')
  @Input()
  size: EarningsSize = IconSize.SMALL;

  /** Change the [size] from tablet and above */
  @HostBinding('attr.data-size-tablet')
  @Input()
  sizeTablet?: EarningsSize;

  /** Change the [size] from desktop and above */
  @HostBinding('attr.data-size-desktop')
  @Input()
  sizeDesktop?: EarningsSize;

  /** Change the [size] from desktopLarge and above */
  @HostBinding('attr.data-size-desktop-large')
  @Input()
  sizeDesktopLarge?: EarningsSize;

  ngOnInit(): void {
    this.calculateStyles();
  }

  ngOnChanges(): void {
    if (!this.compact) {
      const boundEarnings = Math.max(Math.min(this.earnings, 10), 0);
      this.blocks = [];
      for (let i = 1; i <= 10; i++) {
        const value =
          i < boundEarnings ? 1 : Math.max(boundEarnings - i + 1, 0);
        // #9C9EA1 is hardcoded here which is not part of our color palette
        // but used in the redesigned version
        const style = `linear-gradient(to right, currentColor ${
          value * 100
        }%, #9C9EA1 ${value * 100}%)`;
        this.blocks.push(style);
      }
    }

    switch (this.size) {
      case IconSize.LARGE:
        this.earningsTextSize = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.earningsTextSize = TextSize.BODY_16;
        break;
      default:
        this.earningsTextSize = TextSize.BODY_16;
    }

    switch (this.sizeTablet) {
      case IconSize.SMALL:
        this.earningsTextSizeTablet = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.earningsTextSizeTablet = TextSize.BODY_16;
        break;
      case IconSize.LARGE:
        this.earningsTextSizeTablet = TextSize.BODY_16;
        break;
      default:
        this.earningsTextSizeTablet = undefined;
    }

    switch (this.sizeDesktop) {
      case IconSize.SMALL:
        this.earningsTextSizeDesktop = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.earningsTextSizeDesktop = TextSize.BODY_16;
        break;
      case IconSize.LARGE:
        this.earningsTextSizeDesktop = TextSize.BODY_16;
        break;
      default:
        this.earningsTextSizeDesktop = undefined;
    }

    switch (this.sizeDesktopLarge) {
      case IconSize.SMALL:
        this.earningsTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      case IconSize.MID:
        this.earningsTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      case IconSize.LARGE:
        this.earningsTextSizeDesktopLarge = TextSize.BODY_16;
        break;
      default:
        this.earningsTextSizeDesktopLarge = undefined;
    }

    this.calculateStyles();
  }

  private calculateStyles(): void {
    this.earningsTextClass = textVariants({
      color:
        this.textColor === 'foreground'
          ? FontColor.FOREGROUND
          : FontColor.LIGHT,
      size: this.earningsTextSize,
      sizeTablet: this.earningsTextSizeTablet,
      sizeDesktop: this.earningsTextSizeDesktop,
    });
  }
}
